import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  input,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessorComponent } from './ValueAccessorComponent';
import { OnlyNumbersDirective } from './only-numbers.directive';
import { InputType } from './input-type';
import { InputButtonType } from './input-button-type';
import { TranslateModule } from '@ngx-translate/core';
import { LabelAnimationDirective } from '@pc/label-animation';
import { AppValidationError } from './validations';
import { InputComponentData } from './input.interface';
import { BusinessUnit, PcButtonIcon, ValueAccessorTypes } from '@pc/common';
import { SpinningLoaderComponent } from '@pc/spinning-loader';

@Component({
  selector: 'pc-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OnlyNumbersDirective,
    TranslateModule,
    LabelAnimationDirective,
    SpinningLoaderComponent
  ],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent extends ValueAccessorComponent {
  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef;
  @ViewChild(LabelAnimationDirective) labelAnimationDirective!: LabelAnimationDirective;
  @Input() type: keyof typeof InputType = 'text';
  @Input() placeholder = '';
  @Input() inputPlaceholder = '';
  @Input() inputButtonType = InputButtonType.NO_BTN;
  @Input() tabIndex: number | null = 0;
  @Input() maxLimit = 999;
  @Input() name: string = this.cid;
  @Input() onlyNumbers = false;
  @Input() allowDecimal = false; //Use with onlyNumbers
  @Input() isAmountInput = false; //Use with onlyNumbers and allowDecimal
  @Input() useUpdateValueEvent = false;
  @Input() icon!: string;
  @Input() isMandatory!: boolean;
  @Input() inputBackgroundColor = '';
  @Input() isSalesInput = false;
  @Input() isCapitalized = false;
  @Input() asteriskPosition: 'before' | 'after' = 'before';

  @Output() emitOnInputBlur = new EventEmitter<ValueAccessorTypes>();
  @Output() updateValueEvent = new EventEmitter<ValueAccessorTypes>();
  public validateOnBlur = input<boolean>(false);
  public isAutoFocus = input<boolean>(false);
  public isMarkAsTouched = input<boolean>(false);
  public recalculateErrorMessage = input<{ value: boolean }>({ value: false });
  public applyAlert = input<boolean>(false);
  public disable = input<boolean>(false);
  public isLoading = input<boolean>(false);
  public businessUnit = input(BusinessUnit.IL);
  public enableAutocomplete = input(false);
  public BusinessUnit = BusinessUnit;

  public isInputFocus = false;
  protected readonly InputButtonType = InputButtonType;

  private valueChanged = false;

  constructor(private changeDetector: ChangeDetectorRef) {
    super(changeDetector);
    effect(() => {
      if (this.isAutoFocus()) {
        // must use setTimeout to focus on the input element. changeDetector.detectChanges() is not working
        // is a common workaround when dealing with timing issues
        setTimeout(() => {
          this.inputElement.nativeElement.focus();
        }, 50);
      }
    });

    effect(() => {
      if (this.isMarkAsTouched()) {
        const validate = !this.validateOnBlur();
        this.markAsTouched(validate);
      }
    });

    effect(() => {
      if (this.recalculateErrorMessage().value) {
        this.markAsTouched();
      }
    });
  }

  public onInputBlur(): void {
    this.isInputFocus = false;
    this.valueChanged = false;

    this.markAsTouched();
    this.emitOnInputBlur.emit(this.value ?? null);
  }

  public onInputPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text')?.trim();
    if (pastedText !== undefined) {
      if (this.onlyNumbers && !this.isValidNumberInput(pastedText, this.allowDecimal)) {
        event.preventDefault();
        return;
      }
      this.value = pastedText;
    }
    event.preventDefault();
  }

  public onInputFocus(): void {
    this.isInputFocus = true;
  }

  public onInputChange(): void {
    if (!this.validateOnBlur()) return;

    if (this.InputError) {
      this.InputError = null;
      this.changeDetector.markForCheck();
    }
  }

  getIconSrc(): string {
    const stringValue = this.value?.toString();
    return !stringValue?.length ? (this.icon ?? PcButtonIcon.SEARCH_MAGNIFIER) : PcButtonIcon.X_CLOSE;
  }

  public copyConstructor(component: InputComponent, inputComponentData: InputComponentData): void {
    if (inputComponentData) {
      const data = inputComponentData as InputComponentData;
      if (data.type) {
        component.type = data.type;
      }
      if (data.value) {
        component.value = data.value;
      }
      if (data.inputButtonType) {
        component.inputButtonType = data.inputButtonType;
      }
      if (data.tabIndex) {
        component.tabIndex = data.tabIndex;
      }
      if (data.maxLimit) {
        component.maxLimit = data.maxLimit;
      }
      if (data.name) {
        component.name = data.name;
      }
      if (data.onlyNumbers) {
        component.onlyNumbers = data.onlyNumbers;
      }
      if (data.isAutoFocus) {
        component.isAutoFocus = data.isAutoFocus;
      }
      if (data.useUpdateValueEvent) {
        component.useUpdateValueEvent = data.useUpdateValueEvent;
      }
      component.placeholder = data.placeholder;

      this.changeDetector.markForCheck();
    }
  }

  public override notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);

      if (this.useUpdateValueEvent && !this.InputError?.error) {
        this.updateValueEvent.emit(this.value);
      }
    }
  }

  public onClearText(): void {
    this.value = '';
    this.valueChanged = true;
    this.InputError = null;
    this.markAsUntouched();
    this.inputElement.nativeElement.focus();
    this.isInputFocus = true;
  }

  protected override validate(): void {
    if (!this.validateOnBlur()) {
      super.validate();
      return;
    }

    if (!this.formControl) {
      return;
    }

    const controlErrors = this.formControl.errors;
    if (!controlErrors) {
      this.InputError = null;
      return;
    }

    if (this.isInputFocus && this.valueChanged) {
      this.InputError = null;
      return;
    }

    let isRequired = false;
    let errorMessage = '';

    for (const keyError of Object.keys(controlErrors)) {
      if (keyError === 'required') {
        isRequired = true;
        errorMessage = AppValidationError.Errors[keyError] || '';
        break;
      }
      errorMessage = AppValidationError.Errors[keyError] || '';
    }

    if (this.touched) {
      this.InputError = new AppValidationError(errorMessage, isRequired);
    }

    this.changeDetector.markForCheck();
  }

  private isValidNumberInput(text: string, isDecimal: boolean): boolean {
    const regex = isDecimal ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9]*$/;

    return regex.test(text);
  }
}
